import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, alpha } from "@mui/material";
import { SearchRounded } from "@mui/icons-material";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useDashboardColors } from "./dashboardColors";
import { useUser } from "../../Query/useUsers";
import { useCurrentWorkspace } from "../../Query/useWorkspace";

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);

export const SEARCH_SHORTCUT_LABEL = isMac ? "⌘ K" : "Ctrl K";

const greetingKey = (hour: number) => {
  if (hour < 12) return "hub.hero.greetingMorning";
  if (hour < 18) return "hub.hero.greetingAfternoon";
  return "hub.hero.greetingEvening";
};

interface HomeHeroProps {
  onOpenSearch: () => void;
}

const HomeHero: React.FC<HomeHeroProps> = ({ onOpenSearch }) => {
  const { t } = useTranslation("home");
  const { colors: workspace } = useWorkspaceColors();
  const { accent, onAccent } = useDashboardColors();
  const { data: user } = useUser();
  const { data: workspaceData } = useCurrentWorkspace();

  const workspaceName = user?.workspace?.name;
  const workspaceIcon = workspaceData?.workspace?.icon_image
    ? `data:image/png;base64,${workspaceData.workspace.icon_image}`
    : null;

  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "20px",
        backgroundColor: workspace.primary_dark,
        backgroundImage: "url(/como_background.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "right center",
        color: color.white,
      }}
    >
      {/* Tints the photo with the workspace colour and keeps text legible. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: [
            // A warm accent glow rising from the bottom left.
            `radial-gradient(ellipse 60% 90% at 8% 120%, ${alpha(accent, 0.45)}, transparent 70%)`,
            `linear-gradient(90deg, ${workspace.primary_dark} 0%, ${alpha(
              workspace.primary_dark,
              0.88,
            )} 45%, ${alpha(workspace.primary_dark, 0.2)} 100%)`,
          ].join(", "),
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 4,
          background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.6)} 40%, transparent 85%)`,
        }}
      />

      <Box
        sx={{
          position: "relative",
          px: { xs: 2.5, md: 5 },
          py: { xs: 3, md: 4.5 },
          maxWidth: 760,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: "16px",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: accent,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 18,
              height: 2,
              borderRadius: "1px",
              backgroundColor: accent,
            }}
          />
          {t(greetingKey(new Date().getHours()), { name: user?.name ?? "" })}
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1,
            fontSize: { xs: 24, md: 32 },
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          {t("hub.hero.title")}
        </Typography>
        <Typography
          sx={{
            mt: 1,
            fontSize: { xs: 14, md: 15 },
            lineHeight: 1.5,
            color: alpha(color.white, 0.82),
            maxWidth: 560,
          }}
        >
          {t("hub.hero.subtitle")}
        </Typography>

        <Box
          component="button"
          type="button"
          onClick={onOpenSearch}
          aria-label={t("hub.search.open")}
          aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
          sx={{
            mt: 3,
            width: "100%",
            maxWidth: 560,
            height: 48,
            px: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            border: 0,
            borderRadius: "12px",
            cursor: "text",
            textAlign: "left",
            backgroundColor: color.white,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
            color: color.gray500,
            fontSize: 14,
            fontFamily: "inherit",
            transition: "box-shadow 0.15s ease",
            "&:hover": { boxShadow: "0 10px 28px rgba(0, 0, 0, 0.26)" },
            "&:focus-visible": {
              outline: `3px solid ${alpha(accent, 0.7)}`,
              outlineOffset: 2,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              width: 32,
              height: 32,
              ml: -0.75,
              flexShrink: 0,
              borderRadius: "9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: accent,
              color: onAccent,
            }}
          >
            <SearchRounded sx={{ fontSize: 20 }} />
          </Box>
          <Box
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t("hub.search.placeholder")}
          </Box>
          <Box
            component="kbd"
            sx={{
              display: { xs: "none", sm: "inline-block" },
              px: 0.75,
              py: 0.25,
              borderRadius: "6px",
              border: `1px solid ${color.gray200}`,
              backgroundColor: color.gray50,
              fontSize: 11,
              fontFamily: "inherit",
              fontWeight: 600,
              color: color.gray500,
            }}
          >
            {SEARCH_SHORTCUT_LABEL}
          </Box>
        </Box>
      </Box>

      {workspaceName && (
        <Box
          sx={{
            position: { xs: "relative", md: "absolute" },
            top: { md: 24 },
            right: { md: 24 },
            mx: { xs: 2.5, md: 0 },
            mb: { xs: 2.5, md: 0 },
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            maxWidth: { xs: "calc(100% - 40px)", md: 280 },
            px: 1.25,
            py: 0.75,
            borderRadius: "999px",
            backgroundColor: alpha(color.white, 0.12),
            border: `1px solid ${alpha(color.white, 0.22)}`,
            backdropFilter: "blur(6px)",
          }}
        >
          {workspaceIcon && (
            <Box
              component="img"
              src={workspaceIcon}
              alt=""
              sx={{
                width: 22,
                height: 22,
                borderRadius: "6px",
                objectFit: "contain",
                backgroundColor: color.white,
              }}
            />
          )}
          <Typography
            noWrap
            sx={{ fontSize: 13, lineHeight: "18px", fontWeight: 600 }}
          >
            {workspaceName}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default HomeHero;
